import { useState, useEffect, useRef, useCallback } from 'react';

export const useBookingSession = (expiryTime, onExpired = null) => {
  const [isExpired, setIsExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const hasTriggeredExpiryRef = useRef(false);
  const onExpiredRef = useRef(onExpired);

  // Update the ref when onExpired changes, but don't trigger re-render
  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  useEffect(() => {
    // Reset the trigger flag when expiryTime changes
    hasTriggeredExpiryRef.current = false;
    
    // If no expiry time provided, consider session as valid
    if (!expiryTime || expiryTime <= 0) {
      setIsExpired(false);
      setTimeLeft(0);
      return;
    }

    const updateSession = () => {
      const now = Date.now();
      const remaining = expiryTime - now;

      if (remaining <= 0) {
        setTimeLeft(0);
        setIsExpired(true);
        
        // Trigger expiry callback only once using ref
        if (!hasTriggeredExpiryRef.current && onExpiredRef.current) {
          hasTriggeredExpiryRef.current = true;
          onExpiredRef.current();
        }
        
        return;
      }

      setTimeLeft(remaining);
      setIsExpired(false);
    };

    // Update immediately
    updateSession();

    // Update every second
    const interval = setInterval(updateSession, 1000);

    return () => clearInterval(interval);
  }, [expiryTime]);

  // Memoize getTimeRemaining to prevent unnecessary re-renders
  const getTimeRemaining = useCallback(() => {
    if (timeLeft <= 0) return '00:00';
    const totalSeconds = Math.floor(timeLeft / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [timeLeft]);

  return {
    isExpired,
    timeLeft,
    hasValidSession: !isExpired && timeLeft > 0,
    getTimeRemaining
  };
};